import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import loadable from '@loadable/component';
import { useParams, Route, Switch, Redirect, useLocation } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { FlexBox, Breadcrumb } from '@quikr/ui';
import { getLocalStorage, setLocalStorage } from '@quikr/ui/src/helpers/common';
import { useStoreUtmParams } from '@quikr/ui/src/hooks';
import { vapData, homesVap, jobsVap, mobilesVap } from '../../gql/vap.js';
import SeoTags from '@quikr/ui/src/common/SeoTag';
import { useImageError } from '../../hooks';
import CustomError from '../../../common/utils/CustomError.js';

const AndroidRedirectBanner = loadable(() => import('../../components/VAP/AndroidRedirectBanner'), {
  ssr: false
});
const VapLoadables = loadable(() => import('../../components/VAP/VapLoadables'), { ssr: false });
const VAPCars = loadable(() => import('../../components/VAP/CommonModule'));
const VAPJobs = loadable(() => import('../../components/VAP/VapJobs'));
const VapHomes = loadable(() => import('../../components/VAP/VapHomes'));

const VapGlobalStyles = createGlobalStyle`
  .vap__subtitle {
    margin: 30px 0 16px;
    color: ${({ theme }) => theme.colors.TextAlpha};
  }
  
  @media screen and (min-width: 768px) {
    .vap__subtitle {
      margin: 32px 0 24px;
    }
  }

  .scroll-down .vap-right-col{
    top: 20px;
  }

  .scroll-up .vap-right-col{
    top: 140px;
  }

  .mob-border {
    @media screen and (max-width: 767px) {
      padding-bottom: 24px;
      position: relative;
      &:after{
        content: '';
        border-bottom: 1px solid ${({ theme }) => theme.colors.Neutral[300]};
        position: absolute;
        bottom: 0;
        left: -12px;
        width: calc(100% + 24px);
        height: 1px;
      }
    }
  }
`;

const VapWrapper = styled(FlexBox.Container)`
  margin-top: 0;
  padding: 0 12px;
  @media screen and (min-width: 768px) {
    margin-top: 112px;
    padding: 0;
  }
`;

const adQuery = {
  jobs: jobsVap,
  homes: homesVap,
  'mobiles-tablets': mobilesVap
};

const VAP = () => {
  const { adId: id, category, projectId, entityId } = useParams();
  const [showAndroidRedirectBanner, setShowAndroidRedirectBanner] = useState(false);
  const variables = {
    id,
    category,
    source: 'SSR_VAP'
  };
  if (category === 'homes') variables.projectid = projectId;
  if (category === 'mobiles-tablets') variables.entityId = entityId;

  const { loading, data, error } = useQuery(adQuery[category] || vapData, {
    variables,
    errorPolicy: 'all'
  });

  if (process.env.BUILD_TARGET === 'server' && error) {
    const logger = require('@quikr/logger');
    logger('error').error(`Error while fetching ads: ${id}`, error);
    if (error.graphQLErrors) {
      throw new CustomError({ fields: { checkAdMeta: true } });
    }
    throw new Error(500);
  }

  const { ad: { cat, city, subcat } = {}, isMobile, isAndroid, abRandValue } = data || {};
  const pageViewed = useRef(false);
  const { pathname } = useLocation();

  useStoreUtmParams();

  useEffect(() => {
    const quikrPattern = /^https:\/\/(?:[a-z]+\.)?quikr\.com/i;
    const isReferrerQuikr = quikrPattern.test(document.referrer);
    // NOTE: 93 is for Jobs
    if (+cat.id !== 93 && isReferrerQuikr && isMobile && isAndroid && abRandValue <= 25) {
      const key = 'androidRedirectBanner';
      const currentTimestamp = Date.now();
      const lsTimestamp = getLocalStorage(key);
      if (lsTimestamp) {
        const days = 1;
        const daysInMilliseconds = days * 24 * 60 * 60 * 1000;
        const expiry = lsTimestamp + daysInMilliseconds;
        if (expiry < currentTimestamp) {
          setLocalStorage(key, currentTimestamp);
          setShowAndroidRedirectBanner(true);
        }
      } else {
        setLocalStorage(key, currentTimestamp);
        setShowAndroidRedirectBanner(true);
      }
    }
  }, [isMobile, isAndroid, abRandValue]);

  useEffect(() => {
    if (data.ad) {
      if (!pageViewed.current) {
        const cat = data.ad.cat.name.replace(/\s|&/g, '');
        const subcat = data.ad.subcat.name.replace(/\s|&/g, '');
        // eslint-disable-next-line no-undef
        gtag('event', 'page_view', {
          page_title: 'VAP',
          event_category: cat,
          event_label: subcat
        });
        pageViewed.current = true;
      }
    }
  }, [data]);
  useImageError(data);

  if (loading) return null;

  if (data.ad.adlink !== pathname) return <Redirect to={data.ad.adlink} />;
  const categories =
    // eslint-disable-next-line max-len
    'cars|cars-bikes|bikes-scooters|pets|matrimonial|community|entertainment|events|home-lifestyle|electronics-appliances|mobiles-tablets';
  return (
    <VapWrapper>
      <VapGlobalStyles />
      <SeoTags meta={data.ad.meta} />
      {showAndroidRedirectBanner && (
        <AndroidRedirectBanner
          cat={cat}
          city={city}
          subcat={subcat}
          onClose={() => setShowAndroidRedirectBanner(false)}
          showPopup={showAndroidRedirectBanner}
        />
      )}
      <Switch>
        <Route
          exact
          path={`/:category(${categories})/:slug/p/:adId`}
          component={({ staticContext }) => <VAPCars staticContext={staticContext} data={data} />}
        />
        <Route
          exact
          path="/:category(jobs)/:slug([0-9a-zA-Z+-.,]+)+W0QQAdIdZ:adId([0-9]+)"
          component={({ staticContext }) => <VAPJobs staticContext={staticContext} data={data} />}
        />
        <Route
          exact
          path="/:category(jobs)/:slug/p/:adId"
          component={({ staticContext }) => <VAPJobs staticContext={staticContext} data={data} />}
        />
        <Route
          exact
          path="/:category(homes)/:slug/p/:adId"
          component={({ staticContext }) => <VapHomes staticContext={staticContext} data={data} />}
        />
        <Route
          exact
          path="/:category(homes)/:slug/p/:adId/:projectId"
          component={({ staticContext }) => <VapHomes staticContext={staticContext} data={data} />}
        />
        <Route
          exact
          path={'/:category(mobiles-tablets)/:slug/p/:adId/:entityId'}
          component={({ staticContext }) => <VAPCars staticContext={staticContext} data={data} />}
        />
      </Switch>
      <VapLoadables type="Secondary" adData={data.ad} />
      {data.isMobile && <Breadcrumb data={data.ad.breadcrumbs || []} />}
    </VapWrapper>
  );
};

export default React.memo(VAP);
